import { useMemo, useState } from 'react';
import { addHours, formatDate, parse, subDays } from 'date-fns';
import { ChevronDownIcon, XIcon } from 'lucide-react';

import { useCustomerBookingCtx } from '@/app/(app)/customers/[id]/customer-booking-provider';
import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { MultiSelect } from '@/components/atoms/multi-select';
import { Popover } from '@/components/atoms/popover';
import { Select } from '@/components/atoms/select';
import { ServiceTypeSuggestion } from '@/components/booking/service-type-logic';
import { useActiveEmployees } from '@/components/providers/employee-provider';
import { useServiceTypeTemplatesCtx } from '@/components/providers/service-templates-provider';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import { CalendarStrip } from '@/components/ui/calendar-strip';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Label } from '@/components/ui/label';

export function CustomerBookingFilters() {
  const { params, setParams, editingServiceEvent } = useCustomerBookingCtx();
  const { customer, serviceTypesById } = useCustomerCtx();
  const yesterday = useMemo(() => subDays(new Date(), 1), []);
  const [showMore, setShowMore] = useState(() => {
    const isParamSet = (arr: unknown[] | undefined) => Array.isArray(arr) && arr.length > 0;
    return isParamSet(params.regionIds) || isParamSet(params.employeeIds);
  });

  const serviceTypeSuggestion = useMemo(() => {
    // for now, service type suggestions only work for new appointments
    if (editingServiceEvent) return null;
    const serviceType = serviceTypesById.get(params.serviceTypeId ?? '');
    if (!serviceType) return null;
    const servicePlan = customer.service_plans.find((servicePlan) => servicePlan.id === serviceType.service_plan_id);
    if (!servicePlan) return null;
    return (
      <ServiceTypeSuggestion
        customer={customer}
        serviceType={serviceType}
        servicePlan={servicePlan}
        selectedDate={params.selectedDate}
      />
    );
  }, [params.serviceTypeId, params.selectedDate, customer, serviceTypesById, editingServiceEvent]);

  return (
    <div className='flex w-full flex-col gap-4'>
      {serviceTypeSuggestion}
      <div className='space-y-2'>
        <Label>Subscription</Label>
        <ServicePlanSelector />
      </div>
      <div className='space-y-2'>
        <Label>Service Type</Label>
        <div className='flex w-full items-center gap-2'>
          <div className='w-full truncate'>
            <ServiceTypeSelector />
          </div>
          <Select
            options={[
              { label: '15m', value: '15' },
              { label: '30m', value: '30' },
              { label: '45m', value: '45' },
              { label: '1h', value: '60' },
              { label: '1h 15m', value: '75' },
              { label: '1h 30m', value: '90' },
              { label: '1h 45m', value: '105' },
              { label: '2h', value: '120' },
            ]}
            value={params.durationMinutes.toString()}
            onChange={(value) => setParams({ durationMinutes: parseInt(value) })}
            className='w-28'
          />
        </div>
      </div>
      <div className='grid grid-cols-2 gap-2'>
        <ArrivalWindowSelector />
      </div>
      <div className='space-y-2'>
        <Label>Skills</Label>
        <SkillSelector selectedIds={params.skillsIds} setSelectedIds={(values) => setParams({ skillsIds: values })} />
      </div>

      {showMore && (
        <>
          <div className='space-y-2'>
            <Label>Regions</Label>
            <RegionSelector
              selectedIds={params.regionIds}
              setSelectedIds={(values) => setParams({ regionIds: values })}
            />
          </div>
          <div className='space-y-2'>
            <Label>Technicians</Label>
            <EmployeeSelector
              selectedIds={params.employeeIds}
              setSelectedIds={(values) => setParams({ employeeIds: values })}
            />
          </div>
        </>
      )}

      <Button variant='outline' size='sm' className='w-fit' onClick={() => setShowMore(!showMore)}>
        {showMore ? 'Less filters' : 'More filters'}
      </Button>

      <div className='mt-4 space-y-2'>
        <CalendarStrip
          disabled={(date) => date < yesterday}
          selected={params.selectedDate}
          onSelect={(date) => setParams({ selectedDate: date })}
        />
      </div>
    </div>
  );
}

const ARRIVAL_WINDOW_OPTIONS = [
  { label: '3-hour windows', value: '3_HOUR_WINDOWS' },
  { label: 'Custom', value: 'CUSTOM' },
];

function ArrivalWindowSelector() {
  const options = useMemo(() => {
    // 5am to 9pm
    return Array.from({ length: 17 }, (_, i) => {
      const d = parse(`${i + 5}:00`, 'HH:mm', new Date());
      return {
        label: formatDate(d, 'h:mm a'),
        value: formatDate(d, 'HH:mm:ss'),
      };
    });
  }, []);
  const { params, setParams } = useCustomerBookingCtx();

  const onStartTimeChange = (value: string) => {
    const startTime = parse(value, 'HH:mm:ss', new Date());
    let endTime = startTime;
    if (params.window?.end_time) {
      endTime = parse(params.window?.end_time, 'HH:mm:ss', new Date());
    }
    if (startTime >= endTime) {
      endTime = addHours(startTime, 1);
    }
    setParams({ window: { start_time: value, end_time: formatDate(endTime, 'HH:mm:ss') } });
  };
  const onEndTimeChange = (value: string) => {
    const endTime = parse(value, 'HH:mm:ss', new Date());
    let startTime = endTime;
    if (params.window?.start_time) {
      startTime = parse(params.window?.start_time, 'HH:mm:ss', new Date());
    }
    if (startTime >= endTime) {
      startTime = addHours(endTime, -1);
    }
    setParams({ window: { start_time: formatDate(startTime, 'HH:mm:ss'), end_time: value } });
  };

  return (
    <div className='col-span-2 space-y-2'>
      <Label>Arrival Window</Label>
      {!params.window && (
        <Select
          className='w-full'
          options={ARRIVAL_WINDOW_OPTIONS}
          value={params.window ? 'CUSTOM' : '3_HOUR_WINDOWS'}
          onChange={(value) =>
            setParams({
              window: value === 'CUSTOM' ? { start_time: '07:00:00', end_time: '10:00:00' } : null,
            })
          }
        />
      )}
      {params.window && (
        <div className='flex w-full items-center gap-2'>
          <Select
            options={options}
            className='w-32'
            value={params.window.start_time}
            onChange={(value) => onStartTimeChange(value)}
          />
          <span className='text-sm text-muted-foreground'>to</span>
          <Select
            options={options}
            className='w-32'
            value={params.window.end_time}
            onChange={(value) => onEndTimeChange(value)}
          />
          <Button variant='ghost' size='icon' className='ml-auto' onClick={() => setParams({ window: null })}>
            <XIcon className='size-4' />
          </Button>
        </div>
      )}
    </div>
  );
}

function ServicePlanSelector() {
  const { customer } = useCustomerCtx();
  const { mode, params, setParams } = useCustomerBookingCtx();
  const options = useMemo(() => {
    const options = customer.service_plans
      .filter((servicePlan) => !servicePlan.cancelled_at)
      .map((servicePlan) => ({
        label: servicePlan.name,
        value: servicePlan.id,
      }));
    return [...options, { label: 'No subscription', value: 'NONE' }];
  }, [customer]);

  return (
    <Select
      className='w-full'
      disabled={mode === 'RESCHEDULE'}
      options={options}
      value={params.servicePlanId ?? 'NONE'}
      onChange={(value) => {
        if (value === 'NONE') {
          setParams({ servicePlanId: null, serviceTypeId: null });
        } else {
          setParams({ servicePlanId: value });
        }
      }}
    />
  );
}

function ServiceTypeSelector() {
  const { customer, serviceTypesById } = useCustomerCtx();
  const { serviceTypeTemplates, serviceTypeTemplatesById } = useServiceTypeTemplatesCtx();
  const { mode, params, setParams } = useCustomerBookingCtx();
  const serviceTypeOptions = useMemo(() => {
    if (!params.servicePlanId) return [];
    return customer.service_types
      .filter((serviceType) => serviceType.service_plan_id === params.servicePlanId)
      .map((serviceType) => ({
        label: serviceType.name,
        value: serviceType.id,
        templateId: serviceType.template_id,
      }));
  }, [customer.service_types, params.servicePlanId]);
  const serviceTypeTemplateOptions = useMemo(() => {
    return serviceTypeTemplates
      .filter((v) => v.status === 'ACTIVE' && !serviceTypeOptions.some((v1) => v1.templateId === v.id))
      .map((serviceTypeTemplate) => ({
        label: serviceTypeTemplate.name,
        value: serviceTypeTemplate.id,
      }));
  }, [serviceTypeTemplates, serviceTypeOptions]);
  const selected = useMemo(() => {
    if (params.serviceTypeId) {
      return serviceTypesById.get(params.serviceTypeId);
    }
    if (params.serviceTypeTemplateId) {
      return serviceTypeTemplatesById.get(params.serviceTypeTemplateId);
    }
    return null;
  }, [params.serviceTypeId, params.serviceTypeTemplateId, serviceTypesById, serviceTypeTemplatesById]);

  return (
    <Popover
      align='start'
      trigger={
        <Button
          variant='outline'
          size='sm'
          disabled={mode === 'RESCHEDULE'}
          className='flex w-full items-center justify-start rounded-md border-[0.5px] border-input shadow-none'
        >
          <span className='truncate'>{selected ? selected.name : 'Service type'}</span>
          <ChevronDownIcon className='ml-auto size-4 text-muted-foreground' />
        </Button>
      }
    >
      <Command>
        <CommandInput placeholder='Search ...' />
        <CommandList>
          {serviceTypeOptions.length > 0 && (
            <CommandGroup heading="Customer's service types">
              {serviceTypeOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() =>
                    setParams({
                      serviceName: option.label,
                      serviceTypeId: option.value,
                      serviceTypeTemplateId: option.templateId,
                    })
                  }
                >
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {serviceTypeTemplateOptions.length > 0 && (
            <CommandGroup heading='All service types'>
              {serviceTypeTemplateOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() =>
                    setParams({ serviceName: option.label, serviceTypeTemplateId: option.value, serviceTypeId: null })
                  }
                >
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </Popover>
  );
}

function RegionSelector({
  selectedIds,
  setSelectedIds,
}: {
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}) {
  const { office } = useOfficeCtx();
  const options = useMemo(() => {
    return office.regions
      .map((region) => ({
        label: region.name,
        value: region.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [office]);

  return (
    <MultiSelect
      className='w-full'
      label='Regions'
      options={options}
      selected={selectedIds}
      onChange={(values) => setSelectedIds(values)}
    />
  );
}

function SkillSelector({
  selectedIds,
  setSelectedIds,
}: {
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}) {
  const { office } = useOfficeCtx();
  const options = useMemo(() => {
    return office.skills
      .map((skill) => ({
        label: skill.name,
        value: skill.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [office]);

  return (
    <MultiSelect
      className='w-full'
      label='Skills'
      options={options}
      selected={selectedIds}
      onChange={(values) => setSelectedIds(values)}
    />
  );
}

interface EmployeeSelectorProps {
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}

function EmployeeSelector({ selectedIds, setSelectedIds }: EmployeeSelectorProps) {
  const { params } = useCustomerBookingCtx();
  const activeEmployees = useActiveEmployees({ sortBy: 'first_name' });

  const options = useMemo(() => {
    return activeEmployees
      .filter((employee) => employee.bookable)
      .map((employee) => ({
        label: `${employee.first_name} ${employee.last_name}`.trim(),
        value: employee.id,
        disabled: params.blockedEmployeeIds.includes(employee.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activeEmployees, params.blockedEmployeeIds]);

  return (
    <MultiSelect
      label='Technicians'
      options={options}
      selected={selectedIds}
      onChange={(values) => setSelectedIds(values)}
      className='w-full justify-start'
    />
  );
}
